<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Prescription</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goBack">Consultation</a></li>
                      <li class="breadcrumb-item active">Prescription</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading...</p>
            </div>

            <div v-else class="row">
              <!-- Left Column: Patient Info & Allergies -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-account me-2 text-primary"></i>Patient</h6>
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-md me-3">
                          <span class="avatar-title rounded-circle" :class="visit.patientGender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.2rem;">
                            {{ getInitials(visit.patientName) }}
                          </span>
                      </div>
                      <div>
                        <h5 class="mb-1">{{ visit.patientName }}</h5>
                        <span class="text-muted">{{ visit.patientGender }} • {{ visit.patientAge }} yrs • {{ visit.patientWeight || '-' }} kg</span>
                      </div>
                    </div>

                    <!-- Allergies Warning -->
                    <div v-if="visit.allergies" class="alert alert-danger py-2 mb-3">
                      <i class="mdi mdi-alert-circle me-1"></i>
                      <strong>Allergies:</strong> {{ visit.allergies }}
                    </div>

                    <!-- Diagnosis -->
                    <div v-if="consultation.provisionalDiagnosis" class="mb-3">
                      <small class="text-muted d-block mb-1">Diagnosis</small>
                      <p class="mb-0">{{ consultation.provisionalDiagnosis }}</p>
                    </div>
                  </div>
                </div>

                <!-- Quick Add Common Drugs -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-lightning-bolt me-2 text-primary"></i>Common Drugs</h6>
                    <div v-if="commonDrugs.length > 0" class="d-flex flex-wrap gap-2">
                      <button v-for="drug in commonDrugs" :key="drug.drugID" class="btn btn-sm btn-outline-secondary" @click="quickAddDrug(drug)">
                        {{ drug.genericName || drug.brandName }}
                      </button>
                    </div>
                    <div v-else class="text-muted small">
                      <i class="mdi mdi-information-outline me-1"></i>No common drugs configured
                    </div>
                  </div>
                </div>

                <!-- Prescription Summary -->
                <div class="card" v-if="prescriptionItems.length > 0">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-clipboard-list me-2 text-primary"></i>Summary</h6>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Total Items</span>
                      <span class="fw-bold">{{ prescriptionItems.length }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Est. Total</span>
                      <span class="fw-bold text-primary">KES {{ calculateTotal() }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Prescription Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-pill me-2 text-primary"></i>Write Prescription</h5>
                        <small class="text-muted">Dr. {{ sessionNames }}</small>
                      </div>
                    </div>

                    <!-- Drug Search -->
                    <div class="mb-4">
                      <label class="form-label">Search Drug</label>
                      <div class="input-group">
                        <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                        <input type="text" class="form-control" v-model="drugSearch" @input="searchDrugs"
                               placeholder="Search by drug name or generic name...">
                      </div>

                      <!-- Drug Search Results -->
                      <div v-if="drugResults.length > 0" class="drug-results mt-2">
                        <div v-for="drug in drugResults" :key="drug.drugID" class="drug-item" @click="selectDrug(drug)">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <span class="fw-medium">{{ drug.genericName || drug.brandName }}</span>
                              <small class="text-muted d-block">{{ drug.genericName }} | {{ drug.drugForm }} {{ drug.strength }}</small>
                            </div>
                            <div class="text-end">
                              <span class="badge bg-light text-dark">KES {{ drug.unitPrice }}</span>
                              <small class="text-muted d-block">Stock: {{ drug.stockQuantity || 0 }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Selected Drug Form -->
                    <div v-if="selectedDrug" class="selected-drug-form mb-4 p-3 border rounded">
                      <div class="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h6 class="mb-0">{{ selectedDrug.genericName || selectedDrug.brandName }}</h6>
                          <small class="text-muted">{{ selectedDrug.genericName }} | {{ selectedDrug.drugForm }} {{ selectedDrug.strength }}</small>
                        </div>
                        <button type="button" class="btn-close" @click="selectedDrug = null"></button>
                      </div>

                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label">Dosage <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model="currentItem.dosage" placeholder="e.g., 500mg, 1 tablet">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">Frequency <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="currentItem.frequency">
                            <option value="">Select frequency...</option>
                            <option value="OD">Once daily (OD)</option>
                            <option value="BD">Twice daily (BD)</option>
                            <option value="TDS">Three times daily (TDS)</option>
                            <option value="QID">Four times daily (QID)</option>
                            <option value="STAT">Immediately (STAT)</option>
                            <option value="PRN">As needed (PRN)</option>
                            <option value="HS">At bedtime (HS)</option>
                            <option value="AC">Before meals (AC)</option>
                            <option value="PC">After meals (PC)</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">Route</label>
                          <select class="form-select" v-model="currentItem.route">
                            <option value="PO">Oral (PO)</option>
                            <option value="IV">Intravenous (IV)</option>
                            <option value="IM">Intramuscular (IM)</option>
                            <option value="SC">Subcutaneous (SC)</option>
                            <option value="TOP">Topical</option>
                            <option value="INH">Inhalation</option>
                            <option value="PR">Rectal (PR)</option>
                            <option value="SL">Sublingual (SL)</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">Duration <span class="text-danger">*</span></label>
                          <div class="input-group">
                            <input type="number" class="form-control" v-model="currentItem.durationDays" min="1" placeholder="Days">
                            <span class="input-group-text">days</span>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">Quantity <span class="text-danger">*</span></label>
                          <input type="number" class="form-control" v-model="currentItem.quantity" min="1" placeholder="Total qty">
                        </div>
                        <div class="col-12">
                          <label class="form-label">Instructions</label>
                          <textarea class="form-control" rows="2" v-model="currentItem.instructions" placeholder="Special instructions for patient..."></textarea>
                        </div>
                        <div class="col-12">
                          <button type="button" class="btn btn-primary" @click="addToPrescription" :disabled="!canAddItem">
                            <i class="mdi mdi-plus me-1"></i> Add to Prescription
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Prescription Items List -->
                    <div v-if="prescriptionItems.length > 0">
                      <h6 class="mb-3">Prescription Items ({{ prescriptionItems.length }})</h6>
                      <div class="table-responsive">
                        <table class="table table-bordered mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>#</th>
                            <th>Drug</th>
                            <th>Dosage</th>
                            <th>Frequency</th>
                            <th>Duration</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th style="width: 50px;"></th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(item, index) in prescriptionItems" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>
                              <strong>{{ item.drugName }}</strong>
                              <small class="text-muted d-block">{{ item.genericName }}</small>
                            </td>
                            <td>{{ item.dosage }}</td>
                            <td>{{ item.frequency }}</td>
                            <td>{{ item.durationDays }} days</td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ item.totalPrice }}</td>
                            <td>
                              <button type="button" class="btn btn-sm btn-link text-danger" @click="removeItem(index)">
                                <i class="mdi mdi-delete"></i>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="text-center py-5">
                      <div class="empty-state-icon mb-3">
                        <i class="mdi mdi-pill"></i>
                      </div>
                      <h6 class="mb-2">No drugs added yet</h6>
                      <p class="text-muted mb-0">Search and add drugs to the prescription</p>
                    </div>

                    <!-- Prescription Notes -->
                    <div class="mt-4" v-if="prescriptionItems.length > 0">
                      <label class="form-label">Prescription Notes (Optional)</label>
                      <textarea class="form-control" rows="2" v-model="prescriptionNotes" placeholder="Additional notes..."></textarea>
                    </div>

                    <!-- Action Buttons -->
                    <div class="d-flex justify-content-between align-items-center pt-4 border-top mt-4">
                      <button type="button" class="btn btn-light" @click="goBack">
                        <i class="mdi mdi-arrow-left me-1"></i> Back
                      </button>
                      <div class="d-flex gap-2">
                        <button type="button" class="btn btn-outline-secondary" @click="printPrescription" :disabled="prescriptionItems.length === 0">
                          <i class="mdi mdi-printer me-1"></i> Print Preview
                        </button>
                        <button type="button" class="btn btn-success" @click="savePrescription" :disabled="prescriptionItems.length === 0 || saving">
                          <span v-if="saving"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
                          <span v-else><i class="mdi mdi-check me-1"></i> Save Prescription</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "Prescriptions",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      saving: false,
      currentVisitID: '',
      visit: {},
      consultation: {},

      // Drug search
      drugSearch: '',
      drugResults: [],
      selectedDrug: null,
      searchTimeout: null,

      // Current item being added
      currentItem: {
        dosage: '',
        frequency: '',
        route: 'PO',
        durationDays: '',
        quantity: '',
        instructions: ''
      },

      // Prescription items
      prescriptionItems: [],
      prescriptionNotes: '',

      // Common drugs for quick add
      commonDrugs: [],

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      doctorID: ''
    };
  },
  computed: {
    canAddItem() {
      return this.selectedDrug &&
          this.currentItem.dosage &&
          this.currentItem.frequency &&
          this.currentItem.durationDays &&
          this.currentItem.quantity;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    goBack() {
      this.$router.back();
    },

    // Safe toast helper methods
    showSuccess(message) {
      if (this.$refs.toast) {
        this.$refs.toast.success(message);
      } else {
        console.log('Success:', message);
      }
    },

    showError(message) {
      if (this.$refs.toast) {
        this.$refs.toast.error(message);
      } else {
        console.error('Error:', message);
        alert(message);
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    loadVisit() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success && res.data.data.visit) {
              this.visit = res.data.data.visit;
              this.loadConsultation();
            } else {
              this.showError('Visit not found');
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    loadConsultation() {
      const data = new FormData();
      data.append("function", "getConsultation");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success && res.data.data.consultation) {
              this.consultation = res.data.data.consultation;
            }
          })
          .catch((e) => console.error(e));
    },

    loadCommonDrugs() {
      const data = new FormData();
      data.append("function", "getCommonDrugs");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.commonDrugs = res.data.data.drugs || [];
            }
          })
          .catch((e) => console.error(e));
    },

    searchDrugs() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);

      if (this.drugSearch.length < 2) {
        this.drugResults = [];
        return;
      }

      this.searchTimeout = setTimeout(() => {
        const data = new FormData();
        data.append("function", "searchDrugs");
        data.append("facilityID", this.facilityID);
        data.append("searchTerm", this.drugSearch);

        execute(data, hospital)
            .then((res) => {
              if (res.data.success) {
                this.drugResults = res.data.data.drugs || [];
              }
            })
            .catch((e) => console.error(e));
      }, 300);
    },

    selectDrug(drug) {
      this.selectedDrug = drug;
      this.drugSearch = '';
      this.drugResults = [];
      // Pre-fill common values
      this.currentItem = {
        dosage: drug.strength || '',
        frequency: '',
        route: drug.route || 'PO',
        durationDays: '',
        quantity: '',
        instructions: drug.instructions || ''
      };
    },

    quickAddDrug(drug) {
      this.selectDrug(drug);
    },

    addToPrescription() {
      if (!this.canAddItem) return;

      const unitPrice = parseFloat(this.selectedDrug.unitPrice) || 0;
      const quantity = parseInt(this.currentItem.quantity) || 0;

      this.prescriptionItems.push({
        drugID: this.selectedDrug.drugID,
        drugCode: this.selectedDrug.drugCode,
        drugName: this.selectedDrug.genericName || this.selectedDrug.brandName,
        genericName: this.selectedDrug.genericName,
        drugForm: this.selectedDrug.drugForm,
        strength: this.selectedDrug.strength,
        dosage: this.currentItem.dosage,
        frequency: this.currentItem.frequency,
        frequencyDetails: this.getFrequencyDetails(this.currentItem.frequency),
        route: this.currentItem.route,
        durationDays: this.currentItem.durationDays,
        duration: this.currentItem.durationDays + ' days',
        quantity: quantity,
        unitPrice: unitPrice,
        totalPrice: (unitPrice * quantity).toFixed(2),
        instructions: this.currentItem.instructions
      });

      // Reset
      this.selectedDrug = null;
      this.currentItem = {
        dosage: '',
        frequency: '',
        route: 'PO',
        durationDays: '',
        quantity: '',
        instructions: ''
      };

      this.showSuccess('Drug added to prescription');
    },

    getFrequencyDetails(freq) {
      const details = {
        'OD': 'Once daily',
        'BD': 'Twice daily',
        'TDS': 'Three times daily',
        'QID': 'Four times daily',
        'STAT': 'Immediately',
        'PRN': 'As needed',
        'HS': 'At bedtime',
        'AC': 'Before meals',
        'PC': 'After meals'
      };
      return details[freq] || freq;
    },

    removeItem(index) {
      this.prescriptionItems.splice(index, 1);
    },

    calculateTotal() {
      return this.prescriptionItems.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0).toFixed(2);
    },

    printPrescription() {
      window.print();
    },

    savePrescription() {
      if (this.prescriptionItems.length === 0) return;

      this.saving = true;

      const data = new FormData();
      data.append("function", "addPrescription");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("consultationID", this.consultation.consultationID || '');
      data.append("patientID", this.visit.patientID);
      data.append("patientName", this.visit.patientName);
      data.append("patientAge", this.visit.patientAge || '');
      data.append("patientWeight", this.visit.patientWeight || '');
      data.append("prescriberID", this.doctorID);
      data.append("prescriberName", this.sessionNames);
      data.append("diagnosis", this.consultation.provisionalDiagnosis || '');
      data.append("allergies", this.visit.allergies || '');
      data.append("prescriptionNotes", this.prescriptionNotes);
      data.append("items", JSON.stringify(this.prescriptionItems));
      data.append("totalAmount", this.calculateTotal());
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.saving = false;
            if (res.data.success) {
              this.showSuccess('Prescription saved successfully');
              setTimeout(() => {
                this.$router.push('/queue-management');
              }, 1500);
            } else {
              this.showError(res.data.message || 'Failed to save prescription');
            }
          })
          .catch((e) => {
            this.saving = false;
            console.error(e);
            this.showError('Error saving prescription');
          });
    }
  },
  mounted() {
    this.currentVisitID = this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.doctorID = sessionStorage.getItem("staffID") || sessionStorage.getItem("userID") || '';

    if (this.currentVisitID) {
      this.loadVisit();
      this.loadCommonDrugs();
    } else {
      this.showError('No visit specified');
      this.goBack();
    }
  }
};
</script>

<style scoped>
/* Avatar */
.avatar-md { width: 4rem; height: 4rem; }
.avatar-title {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%; font-weight: 600; color: #fff;
}

/* Colors */
.bg-pink { background-color: #e83e8c !important; }

/* Drug Results */
.drug-results {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.drug-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f3f3;
  transition: background 0.2s;
}

.drug-item:hover {
  background: #f8f9fa;
}

.drug-item:last-child {
  border-bottom: none;
}

/* Selected Drug Form */
.selected-drug-form {
  background: #f8f9fa;
}

/* Empty State */
.empty-state-icon {
  width: 80px;
  height: 80px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-icon i {
  font-size: 40px;
  color: #adb5bd;
}

/* Card */
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* Table */
.table > :not(caption) > * > * {
  padding: 0.75rem;
}
</style>